/* eslint-disable jsx-a11y/no-autofocus */
import React, { useState } from 'react';
import BucketIcon from '@svg/bucket_icon';
import styled from 'styled-components';

import { Autocomplete, IconButton, Menu, Stack, TextField } from '@mui/material';
import { Label } from '@common/Label';
import { useQuery } from '@tanstack/react-query';
import { getBuckets, GetBucketsParams, TelnyxBucket } from '@requests/buckets';
import { useParams } from 'react-router-dom';
import { BucketRetrievalMetadata } from '../../../tool.types';
import { Button } from '@common/Button';
import { Add, Close } from '@mui/icons-material';
import { AutocompleteContainer, AutocompleteInput, AutocompletePopper } from '@common/Autocomplete/Base';
import { BucketListItem } from '@common/Autocomplete/ListItem/BucketListItem';
import { useTheme } from '../../../../../../../../../styles';
import { RequestError } from '@requests/common';

const BucketItem = styled.div`
  display: flex;
  gap: 0.5rem;

  .bucket {
    display: flex;
    align-items: center;
    border-radius: 6px;
    border: 1px solid ${({ theme }) => theme.line.light};
    gap: 0.5rem;
    padding: 0.75rem 1rem;
    flex: 1;
  }

  .icon {
    display: flex;
    align-items: center;
    justify-content: center;
    height: 16px;
    width: 16px;
    color: ${({ theme }) => theme.primary};
  }

  .label {
    font-size: 15px;
  }
`;

interface Props {
  metadata: BucketRetrievalMetadata;
  setToolMetadata: React.Dispatch<React.SetStateAction<BucketRetrievalMetadata>>;
}

export const BucketRetrievalExecution: React.FC<Props> = ({ metadata, setToolMetadata }) => {
  const theme = useTheme();

  const { service_id } = useParams();

  const [bucketMenu, setBucketMenu] = useState<null | HTMLElement>(null);
  const [bucketSelectOpen, setBucketSelectOpen] = useState<boolean>(false);

  const { data: buckets = [] } = useQuery<
    TelnyxBucket[],
    RequestError,
    TelnyxBucket[],
    [string, string, GetBucketsParams]
  >({
    queryKey: ['globalFiles', 'buckets', { service_id: service_id as string, all: true }],
    queryFn: getBuckets,
  });

  const bucketOptions: { label: string; value: string }[] = buckets
    .map((item: { name: string }) => ({
      label: item.name,
      value: item.name,
      type: 'telnyx',
    }))
    .filter((option: { label: string; value: string }) => !metadata.bucket_ids.includes(option.value));

  const isMaxBuckets = bucketOptions.length === 0;

  const updateNumericField = (paramName: 'max_num_results', value: number) => {
    setToolMetadata((prevState) => ({ ...prevState, [paramName]: value }));
  };

  const addIndex = (bucketName: string) => {
    if (isMaxBuckets) return;
    setToolMetadata((prevState) => ({ ...prevState, bucket_ids: prevState.bucket_ids.concat(bucketName) }));
  };

  const removeIndex = (bucketId: string) => {
    setToolMetadata((prevState) => ({
      ...prevState,
      bucket_ids: prevState.bucket_ids.filter((item) => item !== bucketId),
    }));
  };

  return (
    <Stack spacing={2}>
      <Stack spacing='8px'>
        <Label title='Buckets' tooltip='A list of Telnyx buckets to search for relevant context to the question.' />
        {metadata.bucket_ids.map((bucket_id) => (
          <BucketItem key={bucket_id}>
            <div className='bucket'>
              <div className='icon'>
                <BucketIcon height={16} />
              </div>
              <div className='label'>{bucket_id}</div>
            </div>
            <IconButton
              sx={{ border: `1px solid ${theme.line.light}`, padding: '10px' }}
              onClick={() => removeIndex(bucket_id)}
            >
              <Close sx={{ fontSize: '1.7ch !important' }} />
            </IconButton>
          </BucketItem>
        ))}
        <Button
          onClick={(e) => setBucketMenu(e.currentTarget)}
          startIcon={<Add />}
          disabled={bucketOptions.length === 0}
        >
          Add Bucket
        </Button>
        <Menu
          anchorEl={bucketMenu}
          open={Boolean(bucketMenu) && bucketOptions.length !== 0}
          onClose={() => setBucketMenu(null)}
          transformOrigin={{ vertical: -10, horizontal: 0 }}
          slotProps={{
            paper: {
              sx: {
                width: '510px',
              },
            },
          }}
        >
          <AutocompleteContainer open={bucketSelectOpen}>
            <Autocomplete
              value={null}
              onChange={(_, newValue) => {
                if (!newValue?.value) return;
                setBucketMenu(null);
                setBucketSelectOpen(false);
                addIndex(newValue.value);

                (document?.activeElement as HTMLElement)?.blur();
              }}
              PopperComponent={AutocompletePopper}
              renderOption={(props, option) => <BucketListItem {...props} option={option} />}
              autoHighlight
              autoFocus
              openOnFocus
              options={bucketOptions}
              disablePortal
              getOptionLabel={(option) => option?.label || ''}
              renderInput={(params) => (
                <AutocompleteInput
                  open={true}
                  ref={params.InputProps.ref}
                  inputProps={params.inputProps}
                  autoFocus
                  placeholder='Select a bucket'
                />
              )}
            />
          </AutocompleteContainer>
        </Menu>
      </Stack>
      <Stack spacing='8px'>
        <Label
          title='Max Results'
          tooltip='The maximum number of results to retrieve as context for the language model.'
        />
        <TextField
          size='small'
          fullWidth
          placeholder='Max Results'
          value={metadata.max_num_results}
          type='number'
          onChange={(e) => {
            if (Number.parseInt(e.target.value) < 0) return;
            updateNumericField('max_num_results', Number.parseInt(e.target.value));
          }}
        />
      </Stack>
    </Stack>
  );
};
